import { Box, SimpleGrid, useToast } from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { SingleField } from '@duke-hq/ui/components/SingleField/SingleField';
import { FormModalWrapper } from '@duke-hq/ui/components/Modals/FormModalWrapper';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useCallback, useState } from 'react';
import { getZodFieldErrorMessages } from '../../lib/zodErrorMessages';
import { BasicDetailsUpdateForm } from './BasicDetailsUpdateForm';
import { SectionHeading } from '../../components/SectionHeading';

export default function BasicDetailsView({
  studentId,
  firstName,
  lastName,
  preferredName,
  email,
  secondaryEmail,
  phoneNumber,
  universalStudentIdentifier,
  isUsiVerified = false,
  hasAccreditedEnrolment = false,
  shouldRefetchAvetmiss = false,
}: {
  studentId: string;
  firstName: string;
  lastName: string | null;
  preferredName?: string | null;
  email: string | null;
  secondaryEmail: string | null;
  phoneNumber: string | null;
  universalStudentIdentifier?: string | null;
  isUsiVerified?: boolean;
  hasAccreditedEnrolment?: boolean;
  shouldRefetchAvetmiss?: boolean;
}): React.ReactElement {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toast = useToast();
  const trpcUtils = trpcAdmin.useUtils();

  const hasUsi = universalStudentIdentifier != null && universalStudentIdentifier !== '';

  const usiStatus = {
    success: hasUsi && isUsiVerified ? t`USI verified` : undefined,
    warning:
      !isUsiVerified && hasAccreditedEnrolment
        ? hasUsi
          ? t`USI not verified. The student cannot continue their accredited course until it is verified.`
          : t`No USI on record. The student cannot continue their accredited course until a USI is verified.`
        : undefined,
    info: hasUsi && !isUsiVerified && !hasAccreditedEnrolment ? t`USI not verified` : undefined,
  };

  const updateBasicInfo = trpcAdmin.students.updateStudentBasicInfo.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Student details updated successfully.`,
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      setIsModalOpen(false);
      await trpcUtils.students.byId.refetch({ id: studentId });
    },
    onError: (error) => {
      const zodErrors = getZodFieldErrorMessages(error);
      toast({
        title: t`Error updating student details.`,
        description:
          zodErrors.length > 0
            ? zodErrors.join(' ')
            : error instanceof Error
              ? error.message
              : t`Failed to update student details`,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
    onSettled: () => {
      if (shouldRefetchAvetmiss) {
        void trpcUtils.avetmiss.sync.refetch();
      }
    },
  });

  const handleUpdate = useCallback(
    (values: {
      firstName: string;
      lastName?: string;
      preferredName?: string;
      email?: string;
      phoneNumber?: string;
      universalStudentIdentifier?: string;
      secondaryEmail?: string;
    }) => {
      updateBasicInfo.mutate({
        id: studentId,
        firstName: values.firstName,
        lastName: values.lastName,
        preferredName: values.preferredName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        universalStudentIdentifier: values.universalStudentIdentifier,
        secondaryEmail: values.secondaryEmail,
      });
    },
    [studentId, updateBasicInfo],
  );

  return (
    <Box>
      <SectionHeading onEdit={() => setIsModalOpen(true)} editDetails={true}>
        <Trans>Basic Information</Trans>
      </SectionHeading>

      <SimpleGrid columns={2} spacing={4}>
        <SingleField name={<Trans>First Name</Trans>} value={firstName} />
        <SingleField name={<Trans>Last name</Trans>} value={lastName} />
        <SingleField name={<Trans>Preferred name</Trans>} value={preferredName} />
      </SimpleGrid>
      <SimpleGrid columns={2} spacing={4} mb={4}>
        <SingleField name={<Trans>Email address</Trans>} value={email} copyToClipboard={true} />
        <SingleField
          name={<Trans>Secondary email address</Trans>}
          value={secondaryEmail}
          copyToClipboard={true}
        />
        <SingleField
          name={<Trans>Phone number</Trans>}
          value={phoneNumber}
          copyToClipboard={true}
        />
        <SingleField
          name={<Trans>USI</Trans>}
          value={universalStudentIdentifier}
          success={usiStatus.success}
          warning={usiStatus.warning}
          info={usiStatus.info}
        />
      </SimpleGrid>

      <FormModalWrapper
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        modalContent={
          <BasicDetailsUpdateForm
            defaultEmail={email}
            defaultSecondaryEmail={secondaryEmail}
            defaultFirstName={firstName}
            defaultLastName={lastName}
            defaultPhoneNumber={phoneNumber}
            defaultPreferredName={preferredName}
            defaultUniversalStudentIdentifier={universalStudentIdentifier}
            isUsiVerified={isUsiVerified}
            updateBasicInfo={handleUpdate}
            onCancel={() => setIsModalOpen(false)}
            isDataSubmitting={updateBasicInfo.isPending}
          />
        }
      />
    </Box>
  );
}
